import { verifyLaunchConfig } from "../api/launchpad-config";
import { keccak256, numberToHex, parseEventLogs, parseTransaction, recoverTransactionAddress, zeroAddress, type Address, type Hex, type TransactionReceipt, type TransactionSerialized } from "viem";
import { z } from "zod";
import { addressSchema, launchChain, metadataDraftHandle, pairs } from "../api/launchpad";
import { createLaunchReadClient, launchContracts, launchpadAbi, launchPairStockFlags, launchRpc, readAntiSniper, type LaunchCall } from "../contracts/launchpad";
import { currentChainId, type Eip1193Provider } from "./eip1193";
import { walletErrorCode } from "./wallet-errors";

const hashSchema = z.custom<Hex>(value => typeof value === "string" && /^0x[\da-f]{64}$/i.test(value));
const decimal = z.string().regex(/^\d+$/);
export const launchMetadataSchema = z.object({
  fields: z.object({ description: z.string(), website: z.string(), telegram: z.string(), x: z.string() }),
  logoHash: z.string().regex(/^[\da-f]{64}$/).nullable(),
  publishedLogo: z.string().nullable().optional(),
  draft: metadataDraftHandle.optional(),
  state: z.enum(["none", "pending", "error", "unknown", "retry", "published", "skipped"]),
});
export type LaunchMetadata = z.infer<typeof launchMetadataSchema>;
const recordSchema = z.object({
  id: z.string().optional(),
  scope: z.string(), account: addressSchema, to: addressSchema,
  state: z.enum(["wallet", "pending", "uncertain", "rejected", "reverted", "confirmed", "event-error", "cancelled", "replaced"]),
  hash: hashSchema.optional(), originalHash: hashSchema.optional(), block: z.number().int().nonnegative().optional(),
  intentBlock: decimal.optional(), token: addressSchema.optional(), inputHash: hashSchema.optional(), value: decimal.optional(), nonce: decimal.optional(),
  verifyMinedCall: z.literal(true).optional(),
  creation: z.object({
    name: z.string(), symbol: z.string(), pair: z.union([z.literal(0), z.literal(1), z.literal(2)]),
    metadata: launchMetadataSchema.optional(),
  }).optional(),
});
type Record = z.infer<typeof recordSchema>;
export const launchStorageKey = "greenwood.launchpad.transactions.v2:" + launchContracts.launchpad;
const storageKey = launchStorageKey;
const persistenceMessage = "Launchpad recovery storage is unavailable. Keep this tab open and check your wallet activity.";
function restore() {
  if (import.meta.env.VITE_APP_MODE === "DEMO") return new Map<string, Record>();
  const raw = sessionStorage.getItem(storageKey);
  const result = raw === null ? [] : z.array(recordSchema).parse(JSON.parse(raw));
  if (new Set(result.map(row => row.scope)).size !== result.length) throw new Error(persistenceMessage);
  return new Map(result.map(row => [row.scope, row.state === "wallet" || row.state === "pending"
    ? { ...row, state: "uncertain" as const } : row]));
}
function createStore() {
  let records: Map<string, Record>;
  let unreadable = false;
  try { records = restore(); } catch { records = new Map(); unreadable = true; }
  return {
    records, unreadable, persistenceFailed: unreadable,
    listeners: new Set<() => void>(), busy: new Set<string>(),
    autoChecking: new Set<string>(), lastChecked: new Map<string, number>(),
  };
}
// Runtime-owned publishers and in-flight receipt checks can outlive a hot update.
// Keep their store and subscriptions shared with newly loaded route modules.
// Only a full page load restores storage and marks interrupted sends uncertain.
const store: ReturnType<typeof createStore> = import.meta.hot?.data?.[storageKey] ?? createStore();
if (import.meta.hot?.data) import.meta.hot.data[storageKey] = store;
const { listeners, busy, autoChecking, lastChecked } = store;
const notify = () => listeners.forEach(listener => listener());
export const subscribeLaunchTx = (listener: () => void) => { listeners.add(listener); return () => { listeners.delete(listener); }; };
export const launchTx = (scope: string) => store.records.get(scope);
export const launchRecords = (): ReadonlyMap<string, Record> => store.records;
export const launchDraftUsed = (draftId: string) => [...store.records.values()].some(row =>
  row.creation?.metadata?.draft?.draftId === draftId && !["rejected", "reverted"].includes(row.state));
export const launchPersistenceFailed = () => store.persistenceFailed;
export const unresolvedLaunchTx = (row: Record | undefined) =>
  row && ["wallet", "pending", "uncertain", "event-error"].includes(row.state);

function save(row: Record, requirePersistence = false, newCreation = false) {
  const next = new Map(store.records);
  if (newCreation) {
    const previous = next.get(row.scope);
    if (previous) {
      const id = previous.id ?? crypto.randomUUID();
      const scope = previous.scope + ":history:" + id;
      next.set(scope, { ...previous, id, scope });
    }
  } else if (row.id && next.get(row.scope)?.id !== undefined && next.get(row.scope)?.id !== row.id) {
    const original = [...next.values()].find(saved => saved.id === row.id);
    if (!original) throw new Error("The saved launch is unavailable.");
    row = { ...row, scope: original.scope };
  }
  next.set(row.scope, row);
  try {
    if (store.unreadable) throw new Error(persistenceMessage);
    sessionStorage.setItem(storageKey, JSON.stringify([...next.values()]));
    store.persistenceFailed = false;
  } catch {
    store.persistenceFailed = true;
    if (requirePersistence) {
      notify();
      throw new Error(persistenceMessage);
    }
  }
  store.records = next;
  notify();
}

function createdToken(receipt: TransactionReceipt, account: Address, context: NonNullable<Record["creation"]>) {
  const events = parseEventLogs({ abi: launchpadAbi, eventName: "TokenCreated",
    logs: receipt.logs.filter(log => log.address.toLowerCase() === launchContracts.launchpad) });
  if (events.length !== 1) return undefined;
  const event = events[0]!.args;
  const pair = pairs[context.pair];
  if (event.creator.toLowerCase() !== account.toLowerCase() || event.name !== context.name ||
      event.symbol !== context.symbol || event.pairId !== context.pair ||
      event.quoteToken.toLowerCase() !== pair.address || event.stockPair !== launchPairStockFlags[context.pair] ||
      event.token.toLowerCase() === zeroAddress) return undefined;
  return event.token;
}

type ChainTx = Awaited<ReturnType<typeof launchRpc.getTransaction>>;
const same = (a: string | null | undefined, b: string) => a?.toLowerCase() === b.toLowerCase();
function matchesCall(row: Record, tx: ChainTx) {
  return same(tx.from, row.account) && same(tx.to, row.to) &&
    keccak256(tx.input) === row.inputHash && tx.value.toString() === row.value;
}
function matchesChain(tx: ChainTx) {
  return tx.chainId === undefined || tx.chainId === launchChain;
}

function finishReceipt(row: Record, receipt: TransactionReceipt, requirePersistence: boolean) {
  if (!same(receipt.to, row.to)) throw new Error("Receipt target mismatch.");
  if (receipt.status !== "success") { save({ ...row, state: "reverted" }, requirePersistence); return; }
  let token: Address | undefined;
  try { token = row.creation && createdToken(receipt, row.account, row.creation); }
  catch { /* Resolve the confirmed launch by hash below; never resend. */ }
  save({ ...row, block: Number(receipt.blockNumber), state: row.creation && !token ? "event-error" : "confirmed", token },
    requirePersistence);
}

async function verifyOriginalProof(row: Record, serialized: string) {
  if (!/^0x(?:[\da-f]{2})+$/i.test(serialized) || serialized.length > 262_146) throw new Error("Invalid signed original transaction.");
  const raw = serialized as Hex;
  if (!row.hash || !same(keccak256(raw), row.hash)) throw new Error("Signed transaction does not match the original hash.");
  let tx: ReturnType<typeof parseTransaction>;
  let sender: Address;
  try {
    tx = parseTransaction(raw);
    sender = await recoverTransactionAddress({ serializedTransaction: raw as TransactionSerialized });
  } catch { throw new Error("Could not verify the signature of the original transaction."); }
  if (tx.chainId !== launchChain || tx.nonce === undefined || !same(tx.to, row.to) ||
      keccak256(tx.data ?? "0x") !== row.inputHash || (tx.value ?? 0n).toString() !== row.value ||
      !same(sender, row.account) ||
      (row.nonce !== undefined && String(tx.nonce) !== row.nonce)) {
    throw new Error("Signed original transaction does not match the saved intent.");
  }
  return { ...row, nonce: String(tx.nonce) };
}

async function recoverIntent(row: Record, hash: Hex) {
  const tx = await launchRpc.getTransaction({ hash });
  if (!same(tx.hash, hash) || !matchesChain(tx) || !matchesCall(row, tx)) {
    throw new Error("Transaction does not match the saved intent.");
  }
  const receipt = await launchRpc.getTransactionReceipt({ hash });
  const block = await launchRpc.getBlock({ blockNumber: receipt.blockNumber });
  if (!same(receipt.transactionHash, hash) || !same(receipt.from, row.account) ||
      receipt.blockHash !== block.hash || tx.blockHash !== block.hash ||
      (row.intentBlock !== undefined && receipt.blockNumber <= BigInt(row.intentBlock))) {
    throw new Error("Could not verify a canonical receipt for this intent.");
  }
  finishReceipt({ ...row, hash, nonce: String(tx.nonce) }, receipt, true);
}

async function reconcile(row: Record, candidate?: Hex, requirePersistence = false) {
  if (!row.hash) return;
  const watchedHash = row.hash;
  try {
    // Record the nonce while the original is still visible. Legacy records can
    // learn their operation identity only from the transaction at the saved hash.
    const original = await launchRpc.getTransaction({ hash: watchedHash }).catch(() => undefined);
    if (original) {
      if (!same(original.hash, watchedHash) || !matchesChain(original) || !same(original.from, row.account) ||
          !same(original.to, row.to) || (row.inputHash && !matchesCall(row, original)) ||
          (row.nonce !== undefined && String(original.nonce) !== row.nonce)) throw new Error("Transaction identity mismatch.");
      row = { ...row, nonce: String(original.nonce), inputHash: keccak256(original.input), value: original.value.toString() };
      save(row);
    }

    let replaced: ChainTx | undefined;
    const receipt = candidate
      ? await launchRpc.getTransactionReceipt({ hash: candidate })
      : await launchRpc.waitForTransactionReceipt({
        hash: watchedHash, timeout: 45_000,
        onReplaced: event => { replaced = event.replacedTransaction; },
      });
    const block = await launchRpc.getBlock({ blockNumber: receipt.blockNumber });
    if (block.hash !== receipt.blockHash || !same(receipt.from, row.account) ||
        (candidate && !same(receipt.transactionHash, candidate)) ||
        ((candidate || row.verifyMinedCall) && row.intentBlock !== undefined && receipt.blockNumber <= BigInt(row.intentBlock))) {
      throw new Error("Receipt identity mismatch.");
    }

    if (!same(receipt.transactionHash, watchedHash)) {
      if (!candidate) {
        if (!replaced || !same(replaced.hash, watchedHash) || !matchesChain(replaced) ||
            !same(replaced.from, row.account) || !same(replaced.to, row.to) ||
            (row.inputHash && !matchesCall(row, replaced)) ||
            (row.nonce !== undefined && String(replaced.nonce) !== row.nonce)) {
          throw new Error("Original transaction could not be verified.");
        }
        row = { ...row, nonce: String(replaced.nonce), inputHash: keccak256(replaced.input), value: replaced.value.toString() };
      }
      if (row.nonce === undefined || !row.inputHash || row.value === undefined) {
        throw new Error("Original transaction nonce could not be verified.");
      }
      const replacement = await launchRpc.getTransaction({ hash: receipt.transactionHash });
      if (!same(replacement.hash, receipt.transactionHash) || !matchesChain(replacement) ||
          !same(replacement.from, row.account) || String(replacement.nonce) !== row.nonce ||
          replacement.blockHash !== receipt.blockHash || replacement.to?.toLowerCase() !== receipt.to?.toLowerCase()) {
        throw new Error("Replacement identity mismatch.");
      }
      row = { ...row, originalHash: row.originalHash ?? watchedHash, hash: replacement.hash };
      if (!matchesCall(row, replacement)) {
        const cancelled = same(replacement.to, row.account) && replacement.value === 0n && replacement.input === "0x";
        save({ ...row, state: cancelled ? "cancelled" : "replaced" }, requirePersistence);
        return;
      }
    } else if (row.verifyMinedCall) {
      const mined = original?.blockHash === block.hash ? original : await launchRpc.getTransaction({ hash: watchedHash });
      if (!same(mined.hash, watchedHash) || !matchesChain(mined) || !matchesCall(row, mined) ||
          mined.blockHash !== block.hash ||
          (row.nonce !== undefined && String(mined.nonce) !== row.nonce)) {
        throw new Error("Mined transaction does not match the saved claim.");
      }
    }
    finishReceipt(row, receipt, requirePersistence);
  } catch (error) {
    if (requirePersistence) throw error;
    save({ ...row, state: "uncertain" });
  }
}

export async function recheckLaunchTx(scope: string, suppliedHash?: string, signedOriginal?: string, originalHash?: string) {
  let row = store.records.get(scope);
  if (busy.has(scope)) throw new Error("The original transaction request is still being tracked.");
  if (!row || !unresolvedLaunchTx(row)) return;
  const candidate = suppliedHash ? hashSchema.parse(suppliedHash) : undefined;
  if (!row.hash && (!candidate || row.state !== "uncertain")) return;
  busy.add(scope);
  try {
    if (await launchRpc.getChainId() !== launchChain) throw new Error("Launchpad RPC chain mismatch.");
    if (!row.hash && candidate) {
      if (!signedOriginal && !originalHash) {
        await recoverIntent(row, candidate);
        return;
      }
      if (!signedOriginal || !originalHash) throw new Error("Provide the original hash and signed original transaction.");
      const hash = hashSchema.parse(originalHash);
      if (same(hash, candidate)) throw new Error("Enter the separate replacement transaction hash.");
      row = await verifyOriginalProof({ ...row, hash }, signedOriginal);
      save(row, true);
      await reconcile(row, candidate, true);
      return;
    }
    if (signedOriginal) {
      row = await verifyOriginalProof(row, signedOriginal);
      save(row, true);
    }
    await reconcile(row, candidate, true);
  } finally { busy.delete(scope); }
}

export async function executeLaunchTx(scope: string, account: Address, provider: Eip1193Provider,
  call: LaunchCall, assertCurrent: () => void, creation?: Record["creation"]) {
  if (import.meta.env.VITE_APP_MODE === "DEMO") throw new Error("Launchpad unavailable in demo.");
  if (store.unreadable) throw new Error(persistenceMessage);
  if (busy.has(scope) || (!creation && unresolvedLaunchTx(store.records.get(scope)))) {
    throw new Error("Check the existing transaction before continuing.");
  }
  busy.add(scope);
  try {
    await verifyLaunchConfig();
    assertCurrent();
    const rpc = createLaunchReadClient(provider);
    const args = { to: call.to, data: call.data, value: call.value, account };
    assertCurrent();
    if (call.antiSniperPool) await readAntiSniper(call.antiSniperPool, account);
    assertCurrent();
    const [rpcChain, gas, gasPrice, balance, intentBlock, result] = await Promise.all([
      rpc.getChainId(), rpc.estimateGas(args), rpc.getGasPrice(), rpc.getBalance({ address: account }),
      rpc.getBlockNumber({ cacheTime: 0 }), rpc.call(args),
    ]);
    assertCurrent();
    if (rpcChain !== launchChain) throw new Error("Launchpad RPC chain mismatch.");
    const gasLimit = (gas * 120n + 99n) / 100n;
    if (gasLimit <= 0n || gasPrice <= 0n || balance < call.value + gasLimit * gasPrice)
      throw new Error("Insufficient HYPE for the amount and estimated gas.");
    if (call.expectsTrue && result.data !== numberToHex(1n, { size: 32 })) throw new Error("Token approval simulation did not return success.");
    assertCurrent();
    if (call.antiSniperPool) await readAntiSniper(call.antiSniperPool, account);
    assertCurrent();
    const [chain, accounts] = await Promise.all([currentChainId(provider), provider.request({ method: "eth_accounts" })]);
    if (!Array.isArray(accounts) || typeof accounts[0] !== "string" || accounts[0].toLowerCase() !== account.toLowerCase()) {
      throw new Error("Wallet account changed before sending.");
    }
    assertCurrent();
    if (chain !== launchChain) throw new Error("Switch to Elysium before sending.");
    const row: Record = { scope, account, to: call.to, creation, state: "wallet",
      ...(creation && { id: crypto.randomUUID() }),
      inputHash: keccak256(call.data), value: call.value.toString(), intentBlock: intentBlock.toString(),
      ...(call.verifyMinedCall && { verifyMinedCall: call.verifyMinedCall }) };
    save(row, true, Boolean(creation));
    try {
      assertCurrent();
    } catch (error) { save({ ...row, state: "rejected" }); throw error; }
    let hash: Hex;
    try {
      hash = hashSchema.parse(await provider.request({ method: "eth_sendTransaction", params: [{
        from: account, to: call.to, data: call.data, value: numberToHex(call.value), gas: numberToHex(gasLimit),
      }] }));
    } catch (error) {
      const rejected = walletErrorCode(error) === 4001;
      save({ ...row, state: rejected ? "rejected" : "uncertain" });
      throw new Error(rejected ? "Wallet request rejected." : creation
        ? "The wallet did not return a transaction hash. This launch remains unverified; you can create another token."
        : "Broadcast outcome unknown. Check wallet activity before continuing.");
    }
    const pending: Record = { ...row, hash, state: "pending" };
    save(pending);
    await reconcile(pending);
    return store.records.get(scope);
  } finally { busy.delete(scope); }
}

export function acknowledgeLaunchWalletActivity(scope: string) {
  const row = store.records.get(scope);
  if (row?.state !== "uncertain" || row.hash || busy.has(scope)) {
    throw new Error("The original transaction is still being tracked or already has a hash.");
  }
  save({ ...row, state: "rejected" }, true);
}

export function setLaunchMetadataState(scope: string, hash: Hex, token: Address | undefined, state: LaunchMetadata["state"], publishedLogo?: string | null, resolvedToken?: Address) {
  const current = store.records.get(scope);
  const row = current?.hash === hash && current.token === token ? current : [...store.records.values()].find(saved =>
    saved.scope.startsWith(scope + ":history:") && saved.hash === hash && saved.token === token);
  if (!row || !["confirmed", "event-error"].includes(row.state) || row.hash !== hash || row.token !== token || !row.creation?.metadata) {
    throw new Error("The launch changed. Reopen recovery.");
  }
  save({ ...row, ...(resolvedToken && { token: resolvedToken, state: "confirmed" as const }), creation: { ...row.creation, metadata: { ...row.creation.metadata, state, publishedLogo } } },
    state === "unknown" || state === "skipped");
}

// Receipt verification and metadata resolution: never rebroadcast or infer rejection.
export async function verifySavedLaunches(account: Address, active: () => boolean) {
  if (!active() || import.meta.env.VITE_APP_MODE === "DEMO" || store.unreadable) return;
  let row = [...store.records.values()].filter(saved => saved.creation && same(saved.account, account) &&
    saved.hash && !(saved.state === "event-error" && saved.block !== undefined && saved.creation?.metadata?.draft) && unresolvedLaunchTx(saved) && !busy.has(saved.scope) && !autoChecking.has(saved.id ?? saved.scope))
    .sort((a, b) => (lastChecked.get(a.id ?? a.scope) ?? 0) - (lastChecked.get(b.id ?? b.scope) ?? 0))[0];
  if (!row) return;
  if (!row.id) { row = { ...row, id: crypto.randomUUID() }; save(row, true); }
  const id = row.id!;
  autoChecking.add(id);
  lastChecked.set(id, Date.now());
  try {
    if (await launchRpc.getChainId() !== launchChain || !active()) return;
    await reconcile(row, undefined, true);
  } finally { autoChecking.delete(id); }
}
